import React from 'react';
import { styled } from '@this/constants/themes';
import { media } from '@this/components/shared/atoms/media';
import type { LegJson } from '@this/src/domain/select_repository';
import type SegmentClass from '@this/src/domain/transit/segment';
import { TransitCompanyImage } from '../../shared/TransitCompanyImage';

interface PackageRouteInfoProps {
  leg: LegJson;
  legIndex: number;
  segment: SegmentClass;
  segmentIdx: number;
  hasAir: boolean;
  omitOuterPadding?: boolean;
}

const PackageRouteInfo: React.FC<PackageRouteInfoProps> = ({
  leg,
  legIndex,
  segment,
  segmentIdx,
  hasAir,
  omitOuterPadding = false
}) => {
  return (
    <StyledSummary $omitOuterPadding={omitOuterPadding}>
      <StyledBoxContent>
        <StyledLegTime>{leg.from.time}</StyledLegTime>
        <StyledBoxTitle>出発</StyledBoxTitle>
        <StyledSpace
          className={
            hasAir || leg.type === 'shin' || leg.type === 'express'
              ? 'select-transport-box__button_exist_leg_station'
              : ''
          }
          $growOnMobile
        >
          <StyledName>{leg.from.name}</StyledName>
        </StyledSpace>
      </StyledBoxContent>

      <StyledBoxContent>
        <StyledMiddleRowLead>
          <StyledLegTimeSpace className="domestic-transport__leg-time-space" />
        </StyledMiddleRowLead>
        <StyledMiddleRowBody>
          <StyledRouteMetaText>
            {leg.airline_name && `${leg.airline_name} `}
            {leg.name}
            <TransitCompanyImage src={segment.imagePathByIndex(segmentIdx)} alt="" />
          </StyledRouteMetaText>
        </StyledMiddleRowBody>
      </StyledBoxContent>

      <StyledBoxContent>
        <StyledLegTime>{leg.to.time}</StyledLegTime>
        <StyledBoxTitle>到着</StyledBoxTitle>
        <StyledSpace
          className={
            hasAir || leg.type === 'shin' || leg.type === 'express'
              ? 'select-transport-box__button_exist_leg_station'
              : ''
          }
          $growOnMobile
        >
          <StyledName>{leg.to.name}</StyledName>
        </StyledSpace>
      </StyledBoxContent>

      <StyledBoardingArrival className="select-transport-box__left__boarding-arrival">
        {segment.shouldShowBoardingStation() &&
          legIndex === 0 && [`乗車駅：${segment.boardingName()}`, <br key={0} />]}
        {segment.shouldShowArrivalStation() &&
          legIndex === segment.legs.length - 1 &&
          `降車駅：${segment.arrivalName()}`}
      </StyledBoardingArrival>
    </StyledSummary>
  );
};

const StyledSummary = styled.div<{ $omitOuterPadding?: boolean }>`
  width: 100%;
  padding: ${props => (props.$omitOuterPadding ? '0' : '8px 5px 8px 10px')};

  ${props =>
    media.spSmall`
      padding: ${props.$omitOuterPadding ? '0' : '10px'};
    `}
`;

const StyledName = styled.div`
  font-size: 16px;
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 400px;

  ${media.spSmall`
    word-break: break-word;
    white-space: normal;
  `}
`;

const StyledLegTime = styled.div`
  min-width: 50px;
  color: #3675ff;
  font-size: 13px;
  height: 16px;
  font-weight: 700;
  line-height: 16px;
  letter-spacing: 0em;
  text-align: left;
  flex-shrink: 0;
  margin-right: 10px;

  ${media.spSmall`
    margin-right: 0;
    height: auto;
    padding-top: 2px;
  `}
`;

const StyledLegTimeSpace = styled.div`
  min-width: 50px;
  color: #3675ff;
  font-size: 13px;
  height: 16px;
  font-weight: 700;
  line-height: 16px;
  letter-spacing: 0em;
  text-align: left;
  flex-shrink: 0;
  margin-right: 10px;

  ${media.spSmall`
    margin-right: 0;
  `}
`;

const StyledBoxTitle = styled.div`
  background: #aaa;
  color: white;
  margin-right: 10px;
  width: 40px;
  height: 20px;
  font-size: 12px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  flex-shrink: 0;
  box-sizing: border-box;
`;

const StyledBoxContent = styled.div`
  display: flex;
  align-items: center;
  height: 2rem;

  ${media.spSmall`
    align-items: flex-start;
    height: auto;
    min-height: 2rem;
    padding-top: 2px;
    padding-bottom: 2px;
  `}
`;

const StyledMiddleRowLead = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;

  ${media.spSmall`
    align-items: flex-start;
  `}
`;

const StyledMiddleRowBody = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 1;

  ${media.spSmall`
    align-items: flex-start;
  `}
`;

const StyledRouteMetaText = styled.span`
  margin-right: 10px;
  font-size: 14px;
  min-width: 0;
  flex: 1;

  ${media.spSmall`
    line-height: 1.4;
    word-break: break-word;
  `}
`;

const StyledSpace = styled.div<{ $growOnMobile?: boolean }>`
  margin-right: 10px;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 400px;

  ${props =>
    media.spSmall`
      ${props.$growOnMobile ? 'flex: 1; min-width: 0;' : ''}
      word-break: break-word;
      white-space: normal;
      line-height: 1.4;
    `}
`;

const StyledBoardingArrival = styled.div`
  font-size: 14px;
`;

export default PackageRouteInfo;
